import { useCallback, useEffect, useMemo, useState } from 'react'

import { DateInput } from '@fullcalendar/core'
import {
  addDays,
  addMinutes,
  isBefore,
  isEqual,
  isSameDay,
  isValid,
  parse,
  parseISO,
  setMinutes,
  startOfDay,
  startOfMinute,
  subMinutes,
} from 'date-fns'
import { format, utcToZonedTime } from 'date-fns-tz'

import { isBookableEvent, isSessionOrSlot } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import { EventExtendedProps } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'

import { useLocaleDateFormat } from './useLocaleDateFormat'

type DayEvent = { startTime: string; endTime: string }
export type Event = {
  start?: DateInput | undefined
  end?: DateInput | undefined
  extendedProps?: Record<string, any> | undefined
}
export type GetEventsFn = (params: { startDate: string; endDate: string; title?: string }) => Promise<Event[]>

const TIME_FORMAT = 'hh:mm a'

type CalendarOptions = {
  sessionDuration: number
  slotMinTime: string
  slotMaxTime: string
  date: string | null
  getEvents?: GetEventsFn
  excludeStartTime?: string
  showSlots?: boolean
  roundUp?: boolean
  stepMinutes?: number
  // When today is selected, offer the full working-hours range instead of starting at the next
  // half hour — lets retro (backdated) bookings target earlier times on the current day.
  includePastTimeSlotsForToday?: boolean
}
export function useCalendarSlots({
  sessionDuration,
  slotMinTime,
  slotMaxTime,
  date,
  getEvents,
  excludeStartTime,
  showSlots,
  roundUp = true,
  stepMinutes = 30,
  includePastTimeSlotsForToday = false,
}: CalendarOptions) {
  const locale = Intl.DateTimeFormat().resolvedOptions().locale
  const localeDateFormat = useLocaleDateFormat()

  const [selectedDateTimes, setSelectedDateTimes] = useState<DayEvent[] | undefined>(undefined)
  const [selectedStartTimes, setSelectedStartTimes] = useState<string[]>([''])
  const [slotMap, setSlotMap] = useState<{ [k: string]: Event }>({})

  const holdDurationMinutes = useMemo(
    () => (roundUp ? Math.ceil(sessionDuration / 30) * 30 : sessionDuration),
    [sessionDuration, roundUp],
  )

  const defaultDate = useMemo(() => format(new Date(), localeDateFormat), [localeDateFormat])
  const selectedDateMidnight = useMemo(
    () => startOfDay(parse(date ?? defaultDate, localeDateFormat, new Date())),
    [date, defaultDate, localeDateFormat],
  )

  const [startDate, endDate] = useMemo(() => {
    const base = parse(date ?? defaultDate, localeDateFormat, new Date()) ?? new Date()
    const startTime = parse(slotMinTime, 'HH:mm:ss', base)
    let endTime = parse(slotMaxTime, 'HH:mm:ss', base)
    // In the case that the endTime is before the start time, indicating working hours extending to midnight, we want to
    // actually track that endtime to be midnight of the next date.
    if (isBefore(endTime, startTime)) {
      endTime = addDays(endTime, 1)
    }
    return [startTime, endTime]
  }, [date, defaultDate, localeDateFormat, slotMinTime, slotMaxTime])

  useEffect(() => {
    if (!getEvents || !date) {
      return
    }
    const start = startOfDay(parse(date, localeDateFormat, new Date()))
    if (!isValid(start)) {
      return
    }
    const base = subMinutes(start, start.getTimezoneOffset())
    getEvents({ startDate: base.toISOString(), endDate: addMinutes(base, 24 * 60).toISOString() }).then((events) => {
      const formatted: DayEvent[] = events
        .filter((e) => {
          const extendedProps = e.extendedProps as EventExtendedProps
          if (showSlots) {
            return extendedProps.lyraEventType === 'session'
          }
          return isSessionOrSlot(extendedProps)
        })
        .map((e) => ({
          startTime: format(utcToZonedTime(parseISO(e.start as string), 'UTC'), TIME_FORMAT),
          endTime: format(utcToZonedTime(parseISO(e.end as string), 'UTC'), TIME_FORMAT),
        }))
        .filter(({ startTime }) => (excludeStartTime ? startTime !== excludeStartTime : true))

      setSlotMap(
        events
          .filter((e) => {
            const extendedProps = e.extendedProps as EventExtendedProps
            return isBookableEvent(extendedProps)
          })
          .reduce(
            (acc, next) => {
              acc[format(utcToZonedTime(parseISO(next.start as string), 'UTC'), TIME_FORMAT)] = next
              return acc
            },
            {} as { [k: string]: Event },
          ),
      )
      setSelectedDateTimes(formatted)
    })
  }, [date, getEvents, localeDateFormat, excludeStartTime, showSlots])

  const blockedRelativeStartTimes = useMemo(() => {
    const n = Math.ceil(sessionDuration / 30) - 1
    const res: number[] = []
    for (let i = -n; i <= n; i++) res.push(i * stepMinutes)
    return res
  }, [sessionDuration, stepMinutes])

  // NOTE: If we ever intend to extend working hours beyond midnight the logic for calculating timeslots below needs to
  // change. We would essentially need to track 2 discreet available windows on the day from startOfDay(startDate) to
  // endDate and then startDate to 12:30AM next date.
  const allAvailableTimeSlots = useMemo(() => {
    const timeSlots: { label: string; value: string }[] = []
    const fmt = (d: Date) =>
      new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: 'numeric', hour12: true }).format(d)

    let currentTime = startDate
    if (!includePastTimeSlotsForToday && isSameDay(selectedDateMidnight, startOfDay(new Date()))) {
      const nextHalf = setMinutes(new Date(), new Date().getMinutes() < 30 ? 30 : 60)
      if (isBefore(nextHalf, startDate)) currentTime = startDate
      else currentTime = nextHalf
    }

    while (
      isBefore(startOfMinute(currentTime), startOfMinute(subMinutes(endDate, holdDurationMinutes))) ||
      isEqual(startOfMinute(currentTime), startOfMinute(subMinutes(endDate, holdDurationMinutes)))
    ) {
      timeSlots.push({
        label: `${fmt(currentTime)} - ${fmt(addMinutes(currentTime, holdDurationMinutes))}`,
        value: format(currentTime, TIME_FORMAT),
      })
      currentTime = addMinutes(currentTime, stepMinutes)
    }

    const blockedFromExisting = selectedDateTimes
      ? [
          ...new Set(
            selectedDateTimes
              .map((e) => e.startTime)
              .reduce((acc: string[], existing) => {
                const blockeds = blockedRelativeStartTimes.map((offset) =>
                  format(addMinutes(parse(existing, TIME_FORMAT, new Date()), offset), TIME_FORMAT),
                )
                return acc.concat(blockeds)
              }, []),
          ),
        ]
      : []

    return timeSlots.filter((t) => !blockedFromExisting.includes(t.value))
  }, [
    endDate,
    holdDurationMinutes,
    locale,
    selectedDateMidnight,
    selectedDateTimes,
    startDate,
    blockedRelativeStartTimes,
    includePastTimeSlotsForToday,
    stepMinutes,
  ])

  const blockedTimeSlotValues: { [k: string]: string[] } = useMemo(() => {
    return selectedStartTimes.reduce((map: { [k: string]: string[] }, selected) => {
      if (!selected) return map
      blockedRelativeStartTimes.forEach((offset) => {
        const blocked = format(addMinutes(parse(selected, TIME_FORMAT, new Date()), offset), TIME_FORMAT)
        blocked in map ? map[blocked].push(selected) : (map[blocked] = [selected])
      })
      return map
    }, {})
  }, [blockedRelativeStartTimes, selectedStartTimes])

  const remainingAvailableTimeslots = useMemo(
    () => allAvailableTimeSlots.filter((t) => !Object.keys(blockedTimeSlotValues).includes(t.value)),
    [allAvailableTimeSlots, blockedTimeSlotValues],
  )

  const getNextTimeSlot = useCallback(() => {
    const last = selectedStartTimes[selectedStartTimes.length - 1]
    if (!last) return ''
    const next = format(addMinutes(parse(last, TIME_FORMAT, new Date()), holdDurationMinutes), TIME_FORMAT)
    return remainingAvailableTimeslots.some((t) => t.value === next) && !selectedStartTimes.includes(next) ? next : ''
  }, [holdDurationMinutes, remainingAvailableTimeslots, selectedStartTimes])

  return {
    date,
    selectedStartTimes,
    setSelectedStartTimes,
    selectedDateTimes,
    setSelectedDateTimes,
    holdDurationMinutes,
    allAvailableTimeSlots,
    blockedTimeSlotValues,
    remainingAvailableTimeslots,
    getNextTimeSlot,
    slotMap,
  }
}
